import { buildReducers, createLazyList, createRequestStatusObject, RequestStatusObject } from '../utils';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '../store';
import { API } from 'aws-amplify';
import {
	OrganisationEducationalSpotlightType,
	OrganisationEducationProfileType,
	OrganisationEmployabilityProfileType,
	OrganisationEmployabilitySpotlightType,
	UserEducationAdvertType,
	StudentEmployabilityProfileType,
	StudentEducationProfileType
} from '../../types/advert';
import { enqueueSnackbar } from 'notistack';
import i18n from '../../i18n';
import { LazyList } from '../../types/types';

export interface AdvertState {
	organisationEducationProfileList: LazyList<OrganisationEducationProfileType>;
	organisationProfile?: OrganisationEducationProfileType | OrganisationEmployabilityProfileType;

	studentProfileEmployability: StudentEmployabilityProfileType;
	studentProfileEducation: StudentEducationProfileType;

	organisationEducationSpotlights: {
		matchedSpotlights: OrganisationEducationalSpotlightType[];
		spotlights: OrganisationEducationalSpotlightType[];
	};
	organisationEmployabilitySpotlights: {
		matchedSpotlights: OrganisationEmployabilitySpotlightType[];
		spotlights: OrganisationEmployabilitySpotlightType[];
	};
	pearsanaMatch: LazyList<OrganisationEducationProfileType>;

	studentEducationProfileList: LazyList<UserEducationAdvertType>;
	studentEmployabilityProfileList: LazyList<StudentEmployabilityProfileType>;

	educationalProfile: StudentEducationProfileType;
	employabilityProfile: StudentEmployabilityProfileType;
	pearsanaMatchCompany: LazyList<OrganisationEmployabilityProfileType>;

	createEducationAdvertRequest: RequestStatusObject;
	createEmployabilityAdvertRequest: RequestStatusObject;
	updateEducationAdvertRequest: RequestStatusObject;
	updateEmployabilityAdvertRequest: RequestStatusObject;

	studentProfileEmployabilityRequest: RequestStatusObject;
	studentProfileEducationRequest: RequestStatusObject;

	organisationEducationProfileListRequest: RequestStatusObject;
	organisationProfileRequest: RequestStatusObject;
	organisationEducationSpotlightsRequest: RequestStatusObject;
	pearsanaMatchRequest: RequestStatusObject;

	studentEducationProfileListRequest: RequestStatusObject;
	studentEmployabilityProfileListRequest: RequestStatusObject;

	showAdvertInterestRequest: RequestStatusObject;
	educationalProfileRequest: RequestStatusObject;
	employabilityProfileRequest: RequestStatusObject;
	pearsanaMatchCompanyRequest: RequestStatusObject;
	organisationEmployabilitySpotlightsRequest: RequestStatusObject;

	isLoading?: boolean;
}

const initialState: AdvertState = {
	organisationEducationProfileList: createLazyList(),
	organisationProfile: undefined,

	studentProfileEmployability: {},
	studentProfileEducation: {},

	organisationEducationSpotlights: { matchedSpotlights: [], spotlights: [] },
	organisationEmployabilitySpotlights: { matchedSpotlights: [], spotlights: [] },
	pearsanaMatch: createLazyList(),
	studentEducationProfileList: createLazyList(),
	studentEmployabilityProfileList: createLazyList(),
	educationalProfile: {},
	employabilityProfile: {},
	pearsanaMatchCompany: createLazyList(),

	createEducationAdvertRequest: createRequestStatusObject(),
	createEmployabilityAdvertRequest: createRequestStatusObject(),
	updateEducationAdvertRequest: createRequestStatusObject(),
	updateEmployabilityAdvertRequest: createRequestStatusObject(),

	studentProfileEmployabilityRequest: createRequestStatusObject(),
	studentProfileEducationRequest: createRequestStatusObject(),

	organisationEducationProfileListRequest: createRequestStatusObject(),
	organisationProfileRequest: createRequestStatusObject(),
	organisationEducationSpotlightsRequest: createRequestStatusObject(),
	pearsanaMatchRequest: createRequestStatusObject(),
	studentEducationProfileListRequest: createRequestStatusObject(),
	studentEmployabilityProfileListRequest: createRequestStatusObject(),
	showAdvertInterestRequest: createRequestStatusObject(),
	educationalProfileRequest: createRequestStatusObject(),
	employabilityProfileRequest: createRequestStatusObject(),
	pearsanaMatchCompanyRequest: createRequestStatusObject(),
	organisationEmployabilitySpotlightsRequest: createRequestStatusObject(),
	isLoading: false
};

export const getStudentProfileEducation = createAsyncThunk<
	StudentEducationProfileType,
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>(
	'adverts/student-education-profile',

	async () => {
		const result = await API.get('inspirusapi', '/adverts', {
			headers: {},
			response: true,
			queryStringParameters: { type: 'educationalAdvert' }
		});
		return result.data[0];
	}
);

export const getStudentProfileEmployability = createAsyncThunk<
	StudentEmployabilityProfileType,
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>(
	'adverts/student-employability-profile',

	async () => {
		const result = await API.get('inspirusapi', '/adverts', {
			headers: {},
			response: true,
			queryStringParameters: { type: 'employabilityAdvert' }
		});
		return result.data[0];
	}
);

export const organisationEducationSpotlights = createAsyncThunk<
	{ matchedSpotlights: OrganisationEducationalSpotlightType[]; spotlights: OrganisationEducationalSpotlightType[] },
	number | string,
	{ dispatch: AppDispatch; state: RootState }
>('adverts/organisation-education-spotlights', async (id) => {
	const result = await API.get('inspirusapi', `/adverts/organisation/educational/profile/${id}/spotlights`, {
		headers: {},
		response: true,
		queryStringParameters: {}
	});
	return result.data;
});

export const organisationEmployabilitySpotlights = createAsyncThunk<
	{
		matchedSpotlights: OrganisationEmployabilitySpotlightType[];
		spotlights: OrganisationEmployabilitySpotlightType[];
	},
	number | string,
	{ dispatch: AppDispatch; state: RootState }
>('adverts/organisation-employability-spotlights', async (id) => {
	const result = await API.get('inspirusapi', `/adverts/organisation/employability/profile/${id}/spotlights`, {
		headers: {},
		response: true,
		queryStringParameters: {}
	});
	return result.data;
});

export const getPearsanaMatch = createAsyncThunk<
	LazyList<OrganisationEducationProfileType>,
	{ spotlightMatch?: boolean; pearsanaMatch?: boolean; search?: any },
	{ dispatch: AppDispatch; state: RootState }
>(
	'adverts/organisations/educational/list',

	async ({ spotlightMatch, pearsanaMatch, search }) => {
		const result = await API.get('inspirusapi', '/adverts/organisations/educational', {
			headers: {},
			response: true,
			queryStringParameters: {
				...(spotlightMatch && { spotlightMatch: 'true' }),
				...(pearsanaMatch && { pearsanaMatch: 'true' }),
				...search
			}
		});

		return {
			items: result.data.items,
			totalCount: result.data.totalCount
		};
	}
);

export const getPearsanaMatchCompany = createAsyncThunk<
	LazyList<OrganisationEmployabilityProfileType>,
	{ spotlightMatch?: boolean; pearsanaMatch?: boolean; search?: any; location?: boolean },
	{ dispatch: AppDispatch; state: RootState }
>(
	'adverts/organisations/employability/list',

	async ({ spotlightMatch, pearsanaMatch, search, location }) => {
		const result = await API.get('inspirusapi', '/adverts/organisations/employability', {
			headers: {},
			response: true,
			queryStringParameters: {
				...(spotlightMatch && { spotlightMatch: 'true' }),
				...(pearsanaMatch && { pearsanaMatch: 'true' }),
				...(location && { location: 'true' }),
				...search
			}
		});

		return {
			items: result.data.items,
			totalCount: result.data.totalCount
		};
	}
);

export const organisationProfile = createAsyncThunk<
	OrganisationEducationProfileType | OrganisationEmployabilityProfileType,
	{ id: number | string; type: string },
	{ dispatch: AppDispatch; state: RootState }
>('adverts/item', async (params) => {
	const result = await API.get('inspirusapi', `/adverts/organisation/profile/${params.id}`, {
		headers: {},
		response: true,
		queryStringParameters: { type: params.type }
	});
	return result.data.Items[0];
});

export const getStudentEducationProfileList = createAsyncThunk<
	LazyList<UserEducationAdvertType>,
	{
		search?: any;
		location?: string;
		pearsanaMatch?: boolean;
		organisationId: string | number;
	},
	{ dispatch: AppDispatch; state: RootState }
>('/adverts/organisation/educational/student', async ({ search, location, pearsanaMatch, organisationId }) => {
	const result = await API.get('inspirusapi', `/adverts/organisation/${organisationId}/educational`, {
		headers: {},
		response: true,
		queryStringParameters: {
			...(location && { location }),
			...(pearsanaMatch && { pearsanaMatch: 'true' }),
			...search
		}
	});
	return {
		items: result.data.items,
		totalCount: result.data.totalCount
	};
});

export const getStudentEmployabilityProfileList = createAsyncThunk<
	LazyList<StudentEmployabilityProfileType>,
	{
		search?: any;
		location?: string;
		pearsanaMatch?: boolean;
		spotlightId?: number | string;
		organisationId: string | number;
	},
	{ dispatch: AppDispatch; state: RootState }
>(
	'/adverts/organisation/employability/student',
	async ({ search, location, pearsanaMatch, organisationId, spotlightId }) => {
		const result = await API.get('inspirusapi', `/adverts/organisation/${organisationId}/employability`, {
			headers: {},
			response: true,
			queryStringParameters: {
				...(location && { location }),
				...(pearsanaMatch && {
					pearsanaMatch: 'true',
					spotlightId: spotlightId
				}),
				...search
			}
		});
		return {
			items: result.data.items,
			totalCount: result.data.totalCount
		};
	}
);

export const getEducationProfileById = createAsyncThunk<
	StudentEducationProfileType,
	{ profileId: string },
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/adverts/educational/by-id',

	async ({ profileId }) => {
		const result = await API.get('inspirusapi', `/adverts/educational/${profileId}`, {
			headers: {},
			response: true,
			queryStringParameters: {}
		});
		return result.data[0];
	}
);

export const getEmployabilityProfileById = createAsyncThunk<
	StudentEmployabilityProfileType,
	{ profileId: string },
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/adverts/employability/by-id',

	async ({ profileId }) => {
		const result = await API.get('inspirusapi', `/adverts/employability/${profileId}`, {
			headers: {},
			response: true,
			queryStringParameters: {}
		});
		return result.data[0];
	}
);

export const createEducationAdvert = createAsyncThunk<
	void,
	UserEducationAdvertType,
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/adverts/create',

	async (payload) => {
		const result = await API.post('inspirusapi', '/adverts/educational', {
			headers: {},
			response: true,
			body: payload
		});
		return result.data;
	}
);

export const createEmployabilityAdvert = createAsyncThunk<any, any, { dispatch: AppDispatch; state: RootState }>(
	'user/adverts/emp/create',

	async (payload) => {
		const result = await API.post('inspirusapi', '/adverts/employability', {
			headers: {},
			response: true,
			body: payload
		});
		return result.data;
	}
);

export const showAdvertInterest = createAsyncThunk<any, any, { dispatch: AppDispatch; state: RootState }>(
	'user/adverts/show/interest',

	async (payload) => {
		const result = await API.post('inspirusapi', '/candidate-interests', {
			headers: {},
			response: true,
			body: payload
		});
		return result.data;
	}
);

export const updateEducationAdvert = createAsyncThunk<
	void,
	{ advertId: string; payload: UserEducationAdvertType },
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/adverts/update',

	async ({ advertId, payload }) => {
		const result = await API.put('inspirusapi', `/adverts/educational/${advertId}`, {
			headers: {},
			response: true,
			body: payload
		});
		return result.data;
	}
);

export const updateEmployabilityAdvert = createAsyncThunk<
	void,
	{ advertId: string; payload: any },
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/adverts/emp/update',

	async ({ advertId, payload }) => {
		const result = await API.put('inspirusapi', `/adverts/employability/${advertId}`, {
			headers: {},
			response: true,
			body: payload
		});
		return result.data;
	}
);

/**
 * @deprecated
 */
export const organisationEducationProfileList = createAsyncThunk<
	LazyList<OrganisationEducationProfileType>,
	any,
	{ dispatch: AppDispatch; state: RootState }
>('adverts/list', async (search) => {
	const result = await API.get('inspirusapi', '/adverts/organisations/educational/list', {
		headers: {},
		response: true,
		queryStringParameters: { ...search }
	});
	return result.data;
});

const AdvertsSlice = createSlice({
	name: 'Adverts',
	initialState,
	reducers: {
		resetAdverts(state) {
			state = initialState;
			return state;
		}
	},
	extraReducers: (builder) => {
		buildReducers(builder, getStudentProfileEmployability, 'studentProfileEmployabilityRequest', {
			onFulfilled: (state, payload) => {
				state.studentProfileEmployability = payload;
			}
		});
		buildReducers(builder, getStudentProfileEducation, 'studentProfileEducationRequest', {
			onFulfilled: (state, payload) => {
				state.studentProfileEducation = payload;
			}
		});
		buildReducers(builder, getStudentEducationProfileList, 'studentEducationProfileListRequest', {
			onFulfilled: (state, payload) => {
				state.studentEducationProfileList = payload;
			}
		});
		buildReducers(builder, getStudentEmployabilityProfileList, 'studentEmployabilityProfileListRequest', {
			onFulfilled: (state, payload) => {
				state.studentEmployabilityProfileList = payload;
			}
		});
		buildReducers(builder, organisationEducationProfileList, 'organisationEducationProfileListRequest', {
			onFulfilled: (state, payload) => {
				state.organisationEducationProfileList = payload;
			}
		});
		buildReducers(builder, organisationProfile, 'organisationProfileRequest', {
			onFulfilled: (state, payload) => {
				state.organisationProfile = payload;
			}
		});
		buildReducers(builder, organisationEducationSpotlights, 'organisationEducationSpotlightsRequest', {
			onFulfilled: (state, payload) => {
				state.organisationEducationSpotlights = payload;
			}
		});
		buildReducers(builder, organisationEmployabilitySpotlights, 'organisationEmployabilitySpotlightsRequest', {
			onFulfilled: (state, payload) => {
				state.organisationEmployabilitySpotlights = payload;
			}
		});
		buildReducers(builder, getPearsanaMatch, 'pearsanaMatchRequest', {
			onFulfilled: (state, payload) => {
				state.pearsanaMatch = payload;
			}
		});
		buildReducers(builder, getPearsanaMatchCompany, 'pearsanaMatchCompanyRequest', {
			onFulfilled: (state, payload) => {
				state.pearsanaMatchCompany = payload;
			}
		});
		buildReducers(builder, getEducationProfileById, 'educationalProfileRequest', {
			onFulfilled: (state, payload) => {
				state.educationalProfile = payload;
			}
		});
		buildReducers(builder, getEmployabilityProfileById, 'employabilityProfileRequest', {
			onFulfilled: (state, payload) => {
				state.employabilityProfile = payload;
			}
		});
		buildReducers(builder, createEducationAdvert, 'createEducationAdvertRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.portal.adverts.successMessage'), { variant: 'success' });
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.portal.adverts.errorMessage'), { variant: 'error' });
			}
		});
		buildReducers(builder, createEmployabilityAdvert, 'createEmployabilityAdvertRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.portal.adverts.successMessage'), { variant: 'success' });
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.portal.adverts.errorMessage'), { variant: 'error' });
			}
		});
		buildReducers(builder, updateEducationAdvert, 'updateEducationAdvertRequest', {
			onPending: (state) => {
				state.isLoading = true;
			},
			onFulfilled: (state, payload) => {
				enqueueSnackbar(i18n.t('pages.portal.adverts.editSuccessMessage'), { variant: 'success' });
				state.isLoading = false;
				state.studentProfileEducation = payload;
			},
			onRejected: (state) => {
				enqueueSnackbar(i18n.t('pages.portal.adverts.editErrorMessage'), { variant: 'error' });
				state.isLoading = false;
			}
		});
		buildReducers(builder, updateEmployabilityAdvert, 'updateEmployabilityAdvertRequest', {
			onPending: (state) => {
				state.isLoading = true;
			},
			onFulfilled: (state) => {
				enqueueSnackbar(i18n.t('pages.portal.adverts.editSuccessMessage'), { variant: 'success' });
				state.isLoading = false;
			},
			onRejected: (state) => {
				enqueueSnackbar(i18n.t('pages.portal.adverts.editErrorMessage'), { variant: 'error' });
				state.isLoading = false;
			}
		});
		buildReducers(builder, showAdvertInterest, 'showAdvertInterestRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.portal.adverts.showInterest'), { variant: 'success' });
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.portal.adverts.errorMessage'), { variant: 'error' });
			}
		});
	}
});

export const { resetAdverts } = AdvertsSlice.actions;

export default AdvertsSlice.reducer;
